import { useMemo } from 'react'
import { CellDataField, CellImagesFilter, PinnedMorphotype } from 'redux/slices'
import { VisualizationSessionConfig } from 'utils/api'
import { useAnalytics } from '../../../services/analytics'
import { useAuth } from '../../../services/auth'
import { useMonitoring } from '../../../services/monitoring'

/*
This module abstracts away the tools we're using for analytics from the rest of the codebase.
This way it becomes very easy to change providers, add new providers, etc.
Simply edit these functions to send events to other providers, e.g., Sentry and DataDog.

TODO: Make sure all the logic for event sending (currently spread around the app) is moved here, creating a separate function for each event
TODO: Once the todo above is completed, make sure to move this file somewhere more shared

Log a google analytics event using a standard event type
    https://support.google.com/firebase/answer/6317498?hl=en&ref_topic=6317484
*/

/**
 * Generic event logging call that sends an event to each analytics provider
 *
 * @TODO if the list of providers grows or we need more complex logic,
 * we may want to consider something like Segment.com where we can make a single API call
 * and have Segment handle compliance and sending events to multiple providers
 */
function createLogEvent(analytics: ReturnType<typeof useAnalytics>, monitoring: ReturnType<typeof useMonitoring>) {
  return (name: string, params?: { [key: string]: unknown }) => {
    analytics.logEvent(name, params)
    monitoring.addAction(name, params)
  }
}

type LogEventFn = (name: string, params?: { [key: string]: unknown }) => void

function createEvents(
  logEvent: LogEventFn,
  analytics: ReturnType<typeof useAnalytics>,
  monitoring: ReturnType<typeof useMonitoring>
) {
  return {
    logEvent,

    setUserEmail(email: string): void {
      analytics.setUserId(email)
      analytics.setUserProperties({ email })
      monitoring.setUser({ email })
    },

    setUserProperty(key: string, value: unknown): void {
      // Convert value to string for analytics
      const stringValue = String(value)
      analytics.setUserProperties({ [key]: stringValue })
      monitoring.setUserProperty(key, value)
    },

  sendDataUploadEvent: (file: File, status: boolean): void => {
    logEvent('new-dataset', {
      filename: file.name,
      size: file.size,
      status,
    })
  },

  sendResetAxisEvent: (filename: string | undefined): void => {
    logEvent('reset-axis', {
      filename,
    })
  },

  sendZoomEvent: (filename: string | undefined, stepInOrOut: 'in' | 'out'): void => {
    logEvent('zoom', {
      filename,
      'step-in-or-out': stepInOrOut,
    })
  },
  sendPanEvent: (filename: string | undefined): void => {
    logEvent('pan', {
      filename,
    })
  },

  sendSaveEvent: (projectCode?: string): void => {
    logEvent('save', {
      projectCode,
    })
  },

  sendExportEvent: (filename: string | undefined): void => {
    logEvent('export', {
      filename,
    })
  },

  sendImportEvent: (file: File, status: boolean): void => {
    logEvent('import', {
      filename: file.name,
      size: file.size,
      status,
    })
  },

  sendLassoEvent: (filename: string, numberOfCells: number): void => {
    logEvent('lasso', {
      filename,
      'number-of-cells': numberOfCells,
    })
  },

  sendRectangleEvent: (filename: string, numberOfCells: number): void => {
    logEvent('rectangle', {
      filename,
      'number-of-cells': numberOfCells,
    })
  },

  sendCustomColorEvent: (numberOfColorsPicked: number): void => {
    logEvent('custom-color', {
      'number-of-colors-picked': numberOfColorsPicked,
    })
  },

  sendPinEvent: (numberOfCells: number, name: string): void => {
    logEvent('pin', {
      'number-of-cells': numberOfCells,
      name,
    })
  },

  sendDeletePinEvent: (name: string): void => {
    logEvent('delete-pin', { name })
  },

  sendPaginationPinEvent: (numberOfCellsPage: number, pageNum: number, name: string): void => {
    logEvent('pagination-pin', {
      'page-num': pageNum,
      'number-of-cells-page': numberOfCellsPage,
      name,
    })
  },

  sendColorByEvent: (option: CellDataField): void => {
    logEvent('color-by', {
      value: option.label,
      'selected-morphometric': option.category === 'Morphometrics',
    })
  },

  sendDisplayImagesByEvent: (cellImagesFilter: CellImagesFilter): void => {
    const { imageSize, displayImages, spacingAdjust } = cellImagesFilter
    if (displayImages) {
      logEvent('display-images', {
        'image-size': imageSize,
        'image-spacing': spacingAdjust,
      })
    }
  },

  sendPinnedCellsEvent: (pinnedMorphotype: PinnedMorphotype): void => {
    logEvent('pinned-cell-group', {
      name: pinnedMorphotype.name,
      numberOfCells: pinnedMorphotype.cells.points?.length,
    })
  },

  sendSelectClusterEvent: (numberOfCells: number, clusterValue: string, source: 'plot' | 'legend'): void => {
    logEvent('select-cluster', {
      numberOfCells,
      clusterValue,
      source,
    })
  },

  sendMergedPinnedCellsEvent: (pinnedMorphotypes: PinnedMorphotype[]): void => {
    pinnedMorphotypes.forEach((pinnedMorphotype) => {
      logEvent('merged-pinned-cell-group', {
        name: pinnedMorphotype.name,
        numberOfCells: pinnedMorphotype.cells.points?.length,
      })
    })
  },

  sendCompareByEvent: (
    dataCategory: string,
    selectedComparisionOptions: CellDataField[],
    dataFieldsToCompare: { [key: string]: string[] }
  ): void => {
    const selectedComparisionCategories = selectedComparisionOptions.map((option) => {
      return option.label
    })
    logEvent('compare-by', {
      'data-category': dataCategory,
      'selected-comparision-categories': JSON.stringify(selectedComparisionCategories),
      'selected-comparision-data-fields': JSON.stringify(dataFieldsToCompare),
    })
  },

  sendContactUsByEvent: (time: number): void => {
    logEvent('contact-us', {
      'contacted-at-time': time,
    })
  },

  sendContactedUsSubmittedEvent: (location: string): void => {
    logEvent('contact-us-submitted', {
      location,
    })
  },

  sendGuideWalkthroughEvent: (skipped?: boolean, titleWhenSkipped?: string): void => {
    if (skipped) {
      logEvent('guide-walkthrough', {
        'title-when-skipped': titleWhenSkipped,
        status: 'incomplete',
      })
    } else {
      logEvent('guide-walkthrough', {
        status: 'complete',
      })
    }
  },

  // demo dataset selections
  sendDemoDatasetEvent: (datasetName: string): void => {
    logEvent('demo-dataset', {
      name: datasetName,
    })
  },

  // session list selection
  sendSessionSelectionEvent: (sessionId: number): void => {
    logEvent('session-selection', { sessionId })
  },

  sendFilterHideAllEvent: () => {
    logEvent('filter-hide-all')
  },
  sendFilterShowAllEvent: () => {
    logEvent('filter-show-all')
  },
  sendFilterHideEvent: (name: string) => {
    logEvent('filter-hide', { name })
  },
  sendFilterShowEvent: (name: string) => {
    logEvent('filter-show', { name })
  },

  // Filters Applied Panel
  sendFilterOpenDetails: () => {
    logEvent('filter-open-details')
  },
  sendFilterOpenDatafield: (field: string) => {
    logEvent('filter-open-datafield', { field })
  },
  sendFilterClearDatafield: (field: string) => {
    logEvent('filter-open-details', { field })
  },

  // start run analysis
  startUploadRunDataTsv: (file: File) => {
    logEvent('start-upload-run-data-tsv', {
      filename: file.name,
      size: file.size,
    })
  },

  confirmUploadRunDataTsv: () => {
    logEvent('confirm-upload-run-data-tsv')
  },

  // Pre-filter
  /**
   *   User starts pre-filtering step
   */
  startPrefilter: (name: string) => {
    logEvent('start-prefilter', { name })
  },
  /**
   *   User chooses to edit the pre-filters from the analysis step
   */
  startEditPrefilter: (name: string) => {
    logEvent('start-edit-prefilter', { name })
  },
  /**
   *   User adds a region to prefilter
   */
  addPrefilterRegion: (name: string, numberOfCells: number) => {
    logEvent('add-prefilter-region', { name, numberOfCells })
  },
  /**
   *   User removes a region to prefilter
   */
  deletePrefilterRegion: () => {
    logEvent('delete-prefilter-region')
  },
  // /**
  //  *   User views more cell images from a region to prefilter
  // TODO This one doesn't make any sense. The user can't modify how they view images
  //  */
  // viewCellImages: () => {
  //   logEvent('view-cell-images')
  // },
  /**
   *   User chooses a field to color by (either Leiden or Density)
   */
  colorBy: (value: string) => {
    logEvent('color-by', { value })
  },
  /**
   *   User chooses to skip pre-filtering (whether in initial pre-filtering flow or edit pre-filter flow)
   */
  skipPrefilter: (name: string) => {
    logEvent('skip-prefilter', { name })
  },
  /**
   * User chooses to save pre-filtering choices (whether in initial or edit pre-filtering flow)
   */
  savePrefilter: (name: string) => {
    logEvent('save-prefilter', { name })
  },

  // Top-up UMAP events
  sendTopUpUmapDialogShown: (params: {
    session_id: number
    version_id: number
    current_cell_count: number
    max_cell_count: number
    delta: number
  }): void => {
    logEvent('top-up-umap-dialog-shown', params)
  },

  sendTopUpUmapRequest: (params: {
    session_id: number
    version_id: number
    current_cell_count: number
    max_cell_count: number
    delta: number
  }): void => {
    logEvent('top-up-umap-request', params)
  },

  sendTopUpUmapCancelled: (params: {
    session_id: number
    version_id: number
    current_cell_count: number
    max_cell_count: number
    delta: number
  }): void => {
    logEvent('top-up-umap-cancelled', params)
  },

  sendTopUpUmapComplete: (params: {
    original_session_id: number
    new_session_id: number
    version_id: number
    duration_seconds: number
  }): void => {
    logEvent('top-up-umap-complete', params)
  },

  differentialFeatures: () => {
    logEvent('differential-features')
  },

  sendMorphotypesEvent: (
    morphotypeIds1: number[] | 'all other cells',
    morphotypeIds2: number[] | 'all other cells'
  ): void => {
    logEvent('differential-features-compare', {
      'c1_morphotypes': {
        morphotypeIds: Array.isArray(morphotypeIds1) ? morphotypeIds1.join(',') : morphotypeIds1,
      },
      'c2_morphotypes': {
        morphotypeIds: Array.isArray(morphotypeIds2) ? morphotypeIds2.join(',') : morphotypeIds2,
      },
    })
  },

  sendResultCount: (count: number) => {
    logEvent('differential-features-top-n-dropdown', {
      'top-features': count,
    })
  },

  sendMultipleTopFeatures: () => {
    logEvent('differential-features-distribution-top-n')
  },

  sendSingleTopFeatureEvent: (feature: string, rank: number) => {
    logEvent('differential-features-distribution-single', {
      'feature': feature,
      'rank': rank,
    });
  },

  sendDeleteDifferentialFeature: () => {
    logEvent('differential-features-delete')
  },

  // Events on the Runs Page
  sendEmbeddingsRequest: (runIds: string, totalCellCount: number) => {
    logEvent('embeddings-request', {
      'run_ids': runIds,
      'total_cell_count': totalCellCount,
    });
  },

  sendEmbeddingsRequestSuccess: (runIds: string, totalCellCount: number) => {
    logEvent('embeddings-request-success', {
      'run_ids': runIds,
      'total_cell_count': totalCellCount,
    });
  },

  // Creates Session Dialog (from Runs Page or Cell Visualization)
  sendCreateSesssionRequest: (config: VisualizationSessionConfig) => {
    const { ...eventParams } = config
    logEvent('create-session', {
      runs: eventParams.runs,
      max_cell_count: eventParams.max_cell_count,
      custom_projection_model_name: eventParams.custom_projection_model_name,
      morphometric_model: eventParams.morphometric_model,
      dl_only_projection: eventParams.dl_only_projection,
      name: eventParams.name,
      sampling_strategy: eventParams.sampling_strategy,
      leiden_resolution: eventParams.leiden_resolution,
      projection_params: JSON.stringify(eventParams.projection_params),
    })
  },

  // Start Analysis Dialog Events
  sendProjectionTypeSelected: (type: string) => {
    logEvent('projection_type_selected', { type })
  },

  sendProjectionParamChanged: (type: string, param: string, value: string) => {
    logEvent('projection_param_changed', {
      type,
      param,
      value,
    })
  },
     sendToggleReorderPlotsEvent: (enabled: boolean): void => {
      logEvent('toggle-reorder-plots', {
        enabled,
      })
    },

    // Experiment Planning Events
    sendExperimentGoalChanged: (params: {
      experimentGoal: 'imaging' | 'purity' | 'yield'
      cellConcentration: number
      cellsToImage?: number
      wells?: Array<{ id: number; outputCells: number; estimatedFrequency: number }>
    }): void => {
      const { experimentGoal, cellConcentration, cellsToImage, wells } = params
      logEvent('experiment_goal_changed', {
        experiment_goal: experimentGoal,
        cell_concentration: cellConcentration,
        cells_to_image: cellsToImage,
        wells: wells ? JSON.stringify(wells.filter(w => w.outputCells > 0)) : undefined,
      })
    },

    // Export Events
    sendCellVisualizationExport: (params: {
      sessionId: number
      morphotypes: Array<{ name: string; cellIds: string[] }>
      exportType: 'png' | 'tiff'
    }): void => {
      const { sessionId, morphotypes, exportType } = params
      logEvent('cell_visualization_export', {
        session_id: sessionId,
        export_type: exportType,
        morphotypes: JSON.stringify(morphotypes.map(m => ({
          name: m.name,
          cell_ids: m.cellIds,
        }))),
      })
    },

    sendCellBrowsingExport: (params: {
      cells: Array<{ runId: string; cellId: string }>
      exportType: 'png' | 'tiff' | 'csv' | 'tsv'
    }): void => {
      const { cells, exportType } = params
      logEvent('cell_browsing_export', {
        export_type: exportType,
        cells: JSON.stringify(cells),
      })
    },
  }
}

const useEventManager = (): ReturnType<typeof createEvents> => {
  const { user, isAuthenticated } = useAuth()
  const analytics = useAnalytics()
  const monitoring = useMonitoring()

  const events = useMemo(() => {
    const logEvent = createLogEvent(analytics, monitoring)
    return createEvents(logEvent, analytics, monitoring)
  }, [analytics, monitoring])

  if (isAuthenticated && user && user.email) {
    events.setUserEmail(user.email)
  }

  return events
}

export default useEventManager
